"use client"

// src/context/ChatContext.tsx
import type React from "react"
import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import axios from "axios"
import { v4 as uuidv4 } from "uuid"

// Types
interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp?: string
  responseType?: "analytics" | "troubleshooting"
  sources?: string[]
  sensorData?: any
  ticketData?: any
  traces?: any[]
  businessView?: any
  agentsRun?: Array<{
    agent: string
    action: string
    result: string
  }>
  responseTime?: number
}

interface ChatSession {
  session_id: string
  title: string
  created_at: string
  last_activity: string
  message_count: number
  last_message: string
}

interface ChatContextType {
  messages: ChatMessage[]
  sessions: ChatSession[]
  currentSessionId: string
  isLoading: boolean
  sources: string[]
  suggestedActions: string[]
  sensorData: any
  ticketData: any
  traces: any[]
  troubleshootingSteps: any[]
  businessView: any | null
  inputMessage: string // For auto-filling from examples

  sendMessage: (message: string) => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  createNewSession: () => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (sessionId: string, newTitle: string) => Promise<void>
  refreshSessions: () => Promise<void>
  clearChat: () => void
  setInputMessage: (message: string) => void // To set input from examples
}

// Create context
const ChatContext = createContext<ChatContextType | undefined>(undefined)

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api"

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem("virtusense_access_token")
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Provider
export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Core state
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => uuidv4())
  const [inputMessage, setInputMessage] = useState<string>('') // For auto-filling examples
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [businessView, setBusinessView] = useState<any | null>(null)

  // Supporting data state
  const [sources, setSources] = useState<string[]>([])
  const [suggestedActions, setSuggestedActions] = useState<string[]>([])
  const [sensorData, setSensorData] = useState<any>(null)
  const [ticketData, setTicketData] = useState<any>(null)
  const [traces, setTraces] = useState<any[]>([])
  const [troubleshootingSteps, setTroubleshootingSteps] = useState<any[]>([])

  // Refs
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isLoadingSession = useRef<boolean>(false)
  const lastRequestRef = useRef<number>(Date.now())
  const [currentAgentsRun, setCurrentAgentsRun] = useState<Array<{ agent: string; action: string; result: string }>>([])
  
  // Request deduplication
  const pendingRequests = useRef<Map<string, Promise<any>>>(new Map())
  
  // Refresh sessions with deduplication
  const refreshSessions = useCallback(async () => {
    // Don't fetch if no token
    const token = localStorage.getItem("virtusense_access_token")
    if (!token) {
      setSessions([])
      return []
    }
    
    const requestKey = 'refreshSessions'
    
    // Check if request is already pending
    if (pendingRequests.current.has(requestKey)) {
      return pendingRequests.current.get(requestKey)
    }
    
    const request = (async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/chat/history`, {
          headers: getAuthHeader(),
        })
        setSessions(response.data.sessions || [])
        return response.data.sessions
      } catch (error: any) {
        // Only log error if it's not a 401 (expected when logged out)
        if (error.response?.status !== 401) {
          console.error("Failed to load sessions:", error)
        }
        setSessions([])
        return []
      } finally {
        pendingRequests.current.delete(requestKey)
      }
    })()
    
    pendingRequests.current.set(requestKey, request)
    return request
  }, [])
  const connectSSE = useCallback((sessionId: string) => {
    if (!sessionId) return

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    try {
      // Determine SSE URL for traces
      let url: string
      if (/^https?:\/\//i.test(API_BASE_URL)) {
        url = `${API_BASE_URL.replace(/\/$/, "")}/traces/${sessionId}`
      } else {
        // For development, connect to backend on port 8000
        const backendOrigin = window.location.origin.replace(/:\d+$/, ":8000")
        url = `${backendOrigin}${API_BASE_URL}/traces/${sessionId}`
      }

      const eventSource = new EventSource(url)

      // Throttle traces to reduce re-renders under heavy streaming
      const bufferRef = { current: new Map<string, any>() }
      const flushTimeoutRef = { current: null as null | number }

      const scheduleFlush = () => {
        if (flushTimeoutRef.current != null) return
        flushTimeoutRef.current = window.setTimeout(() => {
          const buffered = Array.from(bufferRef.current.values())
          if (buffered.length > 0) {
            setTraces((prev) => {
              // replace by step to keep latest for each step
              const steps = new Set(buffered.map((t) => t.step))
              const filtered = prev.filter((t: any) => !steps.has(t.step))
              return [...filtered, ...buffered]
            })
          }
          bufferRef.current.clear()
          flushTimeoutRef.current = null
        }, 120) // ~8fps, smooth enough while typing
      }

      eventSource.onmessage = (event) => {
        try {
          const trace = JSON.parse(event.data)

          // Ignore heartbeat events
          if (trace.status === "heartbeat") return

          // Only include traces after the most recent request
          if (Date.now() < lastRequestRef.current) return

          // Buffer by step; keep latest
          bufferRef.current.set(trace.step, trace)
          scheduleFlush()

          // Collect completed agents for summary (lightweight state)
          if (trace.status === "completed") {
            setCurrentAgentsRun((prev) => [
              ...prev,
              {
                agent: trace.step,
                action: trace.step.replace("_", " "),
                result: trace.details,
              },
            ])
          }
        } catch (error) {
          console.error("Failed to parse trace event:", error)
        }
      }

      eventSource.onerror = () => {
        console.warn("Trace SSE connection error, retrying in 2s")
        eventSource.close()
        // Clear any existing reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
        }
        // Attempt to reconnect after a delay
        reconnectTimeoutRef.current = setTimeout(() => connectSSE(sessionId), 2000)
      }

      eventSourceRef.current = eventSource
    } catch (error) {
      console.error("Failed to connect SSE:", error)
    }
  }, [])
  // Load sessions on mount and connect SSE
  useEffect(() => {
    refreshSessions()
    connectSSE(currentSessionId)
    const interval = setInterval(refreshSessions, 60000) // Refresh every minute
    return () => {
      clearInterval(interval)
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }
  }, [currentSessionId, refreshSessions, connectSSE])

  // Load session
  // Load session
  const loadSession = useCallback(
  async (sessionId: string) => {
    // Prevent multiple simultaneous loads
    if (isLoadingSession.current || sessionId === currentSessionId) {
      return
    }

    isLoadingSession.current = true
    setIsLoading(true)
    
    // Add detailed logging
    // Loading session

    try {
      // Clear current state FIRST
      setMessages([])
      setSources([])
      setSensorData(null)
      setTicketData(null)
      setBusinessView(null)
      setTraces([])
      setTroubleshootingSteps([])

      // Close existing SSE
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      // Load session messages
      const response = await axios.get(`${API_BASE_URL}/chat/session/${sessionId}`, {
        headers: getAuthHeader(),
      })
      
      // Raw session response received

      if (response.data && response.data.messages) {
        // Parse and set messages with proper JSON parsing
        const loadedMessages: ChatMessage[] = response.data.messages.map((msg: any, index: number) => {
          // Processing message
          
          // Parse business_view if it's a string (from database)
          let businessViewData: any = null
          if (msg.business_view) {
            if (typeof msg.business_view === 'string') {
              try {
                businessViewData = JSON.parse(msg.business_view)
                // Parsed business_view successfully
              } catch (e) {
                console.error(`Failed to parse business_view for message ${index}:`, e)
              }
            } else {
              businessViewData = msg.business_view
              // Direct business_view found
            }
          }
          
          // Parse sensor_data if it's a string
          let sensorDataParsed: any = null
          if (msg.sensor_data) {
            if (typeof msg.sensor_data === 'string') {
              try {
                sensorDataParsed = JSON.parse(msg.sensor_data)
              } catch (e) {
                console.error(`Failed to parse sensor_data for message ${index}:`, e)
              }
            } else {
              sensorDataParsed = msg.sensor_data
            }
          }
          
          // Parse ticket_data if it's a string
          let ticketDataParsed: any = null
          if (msg.ticket_data) {
            if (typeof msg.ticket_data === 'string') {
              try {
                ticketDataParsed = JSON.parse(msg.ticket_data)
              } catch (e) {
                console.error(`Failed to parse ticket_data for message ${index}:`, e)
              }
            } else {
              ticketDataParsed = msg.ticket_data
            }
          }
          
          // Parse sources if it's a string
          let sourcesParsed: string[] = []
          if (msg.sources) {
            if (typeof msg.sources === 'string') {
              try {
                const parsed = JSON.parse(msg.sources)
                sourcesParsed = Array.isArray(parsed) ? parsed : []
              } catch (e) {
                sourcesParsed = []
              }
            } else if (Array.isArray(msg.sources)) {
              sourcesParsed = msg.sources
            }
          }

          return {
            id: msg.id,
            role: msg.role as "user" | "assistant",
            content: msg.content,
            timestamp: msg.created_at || msg.timestamp,
            responseType:
              msg.response_type === "analytics" || msg.response_type === "troubleshooting"
                ? msg.response_type
                : "troubleshooting" as "analytics" | "troubleshooting",
            sources: sourcesParsed,
            sensorData: sensorDataParsed,
            ticketData: ticketDataParsed,
            businessView: businessViewData,
            traces: msg.traces || [],
          }
        })

        setMessages(loadedMessages)
        setCurrentSessionId(sessionId)
        
        // Messages loaded successfully

        // Find the MOST RECENT data from any assistant message
        let foundBusinessView: any = null
        let foundSensorData: any = null
        let foundTicketData: any = null
        let foundSources: string[] = []
        
        // Iterate backwards to find the most recent data
        for (let i = loadedMessages.length - 1; i >= 0; i--) {
          const msg = loadedMessages[i]
          
          // Only look at assistant messages for data
          if (msg.role === "assistant") {
            // Get the FIRST (most recent) occurrence of each data type
            if (!foundBusinessView && msg.businessView) {
              foundBusinessView = msg.businessView
              // Found businessView
            }
            if (!foundSensorData && msg.sensorData) {
              foundSensorData = msg.sensorData
              // Found sensorData
            }
            if (!foundTicketData && msg.ticketData) {
              foundTicketData = msg.ticketData
              // Found ticketData
            }
            if (msg.sources && msg.sources.length > 0 && foundSources.length === 0) {
              foundSources = msg.sources
              // Found sources
            }
            
            // If we found all data types, we can stop
            if (foundBusinessView && foundSensorData && foundTicketData && foundSources.length > 0) {
              // Found all data types, stopping search
              break
            }
          }
        }
        
        // Now set all the found data to state
        // Setting restored data to state
        
        // Set states - this will trigger the UI to show the info button
        if (foundBusinessView) {
          // Setting businessView state
          setBusinessView(foundBusinessView)
        }
        if (foundSensorData) {
          setSensorData(foundSensorData)
        }
        if (foundTicketData) {
          setTicketData(foundTicketData)
        }
        if (foundSources.length > 0) {
          setSources(foundSources)
        }

        // Reconnect SSE for new session
        connectSSE(sessionId)
      }

      // Refresh sessions list
      await refreshSessions()
      
      // Force a small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 100))
      
    } catch (error) {
      console.error("Failed to load session:", error)
      // Don't throw - just log the error
    } finally {
      setIsLoading(false)
      isLoadingSession.current = false
      
      // Log final state
      // Session load complete
    }
  },
  [currentSessionId, refreshSessions, connectSSE],
)

  // Create new session - moved after other functions to avoid circular deps
  const createNewSession = useCallback(async () => {
    const newSessionId = uuidv4()

    // Close existing SSE
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    // Clear all state
    setCurrentSessionId(newSessionId)
    setMessages([])
    setSources([])
    setSuggestedActions([])
    setSensorData(null)
    setTicketData(null)
    setTraces([])
    setTroubleshootingSteps([])
    setCurrentAgentsRun([])
    setBusinessView(null)

    // Connect SSE for new session
    connectSSE(newSessionId)

    // Refresh sessions list
    await refreshSessions()
  }, [refreshSessions, connectSSE])

  // Delete session - must come after createNewSession
  const deleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await axios.delete(`${API_BASE_URL}/chat/session/${sessionId}`, {
          headers: getAuthHeader(),
        })

        // If deleting current session, create new one
        if (sessionId === currentSessionId) {
          await createNewSession()
        }

        // Refresh sessions list
        await refreshSessions()
      } catch (error) {
        console.error("Failed to delete session:", error)
        throw error // Propagate error to handle in UI
      }
    },
    [currentSessionId, createNewSession, refreshSessions],
  )

  // Rename session
  const renameSession = useCallback(
    async (sessionId: string, newTitle: string) => {
      try {
        await axios.put(`${API_BASE_URL}/sessions/${sessionId}/rename`, 
          { title: newTitle },
          { headers: getAuthHeader() }
        )
        
        // Update the session in local state
        setSessions(prevSessions => 
          prevSessions.map(session => 
            session.session_id === sessionId 
              ? { ...session, title: newTitle }
              : session
          )
        )
      } catch (error) {
        console.error("Failed to rename session:", error)
        throw error
      }
    },
    []
  )

  // Clear chat
  const clearChat = useCallback(() => {
    createNewSession()
  }, [createNewSession])

  // Connect SSE for traces

  // Send message
  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || isLoading) return

      // Reset traces and agents for new request
      setTraces([])
      setCurrentAgentsRun([])
      lastRequestRef.current = Date.now()

      const userMessage: ChatMessage = {
        id: uuidv4(),
        role: "user",
        content: message,
        timestamp: new Date().toISOString(),
      }

      const startTime = Date.now()
      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)

      try {
        const response = await axios.post(
          `${API_BASE_URL}/chat`,
          {
            message,
            session_id: currentSessionId,
          },
          {
            headers: {
              ...getAuthHeader(),
              "Content-Type": "application/json",
            },
            timeout: 180000, // 3 minutes for SOP troubleshooting agent processing
          },
        )
        // Full API Response received
        const assistantMessage: ChatMessage = {
          id: uuidv4(),
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toISOString(),
          responseType:
            response.data.response_type === "analytics" || response.data.response_type === "troubleshooting"
              ? response.data.response_type
              : ("troubleshooting" as "analytics" | "troubleshooting"),
          sources: response.data.sources || [],
          sensorData: response.data.sensor_data,
          ticketData: response.data.ticket_data,
          businessView: response.data.business_view, // 🔴 ADD THIS LINE
          traces: response.data.traces || [],
          agentsRun: [...currentAgentsRun], // Include agents that ran
          responseTime: Date.now() - startTime,
        }

        setMessages((prev) => [...prev, assistantMessage])

        // Update supporting data
        setSources(response.data.sources || [])
        setSuggestedActions(response.data.suggested_actions || [])
        setSensorData(response.data.sensor_data)
        setTicketData(response.data.ticket_data)
        if (response.data.business_view) {
          // Setting businessView state
          setBusinessView(response.data.business_view)
        } else {
          // No business_view in response
          setBusinessView(null)
        } // 🔴 ADD THIS LINE

        // Keep traces from SSE connection (they're more detailed)
        // Don't overwrite with response.data.traces

        if (response.data.troubleshooting_steps) {
          setTroubleshootingSteps(response.data.troubleshooting_steps)
        }

        // Clear agents run for next message
        setCurrentAgentsRun([])

        // Refresh sessions to update the list
        await refreshSessions()
      } catch (error) {
        console.error("Failed to send message:", error)

        // Add error message
        const errorMessage: ChatMessage = {
          id: uuidv4(),
          role: "assistant",
          content: "Sorry, I encountered an error processing your request. Please try again.",
          timestamp: new Date().toISOString(),
        }

        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setIsLoading(false)
      }
    },
    [currentSessionId, isLoading, refreshSessions, currentAgentsRun],
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  const value: ChatContextType = {
    messages,
    sessions,
    currentSessionId,
    isLoading,
    sources,
    suggestedActions,
    sensorData,
    ticketData,
    traces,
    troubleshootingSteps,
    businessView,
    inputMessage,
    sendMessage,
    loadSession,
    createNewSession,
    deleteSession,
    renameSession,
    refreshSessions,
    clearChat,
    setInputMessage,
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

// Custom hook
export const useChat = () => {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider")
  }
  return context
}
